import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CalendarDays, Clock3, Download, Hand, LogIn, LogOut, MonitorPlay, Power, Printer, Tv, Zap } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type Room } from '../api/client';
import { NeedsSite } from '../app/Layout';
import { useSession, useSite } from '../app/session';
import { Select } from '../components/Select';
import { Empty, ErrorBox, Loading, PageHeader } from '../components/ui';

interface Hour {
  deviceId: string;
  hour: string;
  onlineSec: number;
  watchSec: number;
  keyPresses: number;
  channelChanges: number;
}
interface Snapshot {
  kind: 'check_in' | 'check_out';
  stayId: string;
  guest: string | null;
  online: boolean;
  status: string;
  lastSeenAt: string | null;
  switchedOnAt: string | null;
  channel: { number: number; name: string } | null;
  appVersion: string | null;
  last24h: { onlineSec: number; watchSec: number; keyPresses: number; channelChanges: number; playbackErrors: number; switchedOn: number };
}
interface Report {
  generatedAt: string;
  from: string;
  to: string;
  site: { id: string; name: string; timezone: string };
  room: { id: string; number: string; zone: string | null };
  devices: { id: string; name: string; platform: string; model: string | null; manufacturer: string | null; serial: string | null; macAddress: string | null; appVersion: string | null; status: string; online: boolean; lastSeenAt: string | null; createdAt: string }[];
  hours: Hour[];
  events: { id: string; deviceId: string; type: string; occurredAt: string; data: Record<string, unknown> }[];
  channels: { number: number; name: string | null; count: number }[];
  stays: { id: string; displayName: string | null; reservationId: string | null; checkInAt: string; checkOutAt: string | null; status: string }[];
}

const PERIODS = [
  ['24h', 'Last 24 hours', 1],
  ['7d', 'Last 7 days', 7],
  ['14d', 'Last 14 days', 14],
  ['30d', 'Last 30 days', 30],
] as const;

const hm = (sec: number) => {
  const m = Math.round(sec / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}`;
};

/** Day key and hour of a date in the hotel's time zone. */
function local(iso: string, tz: string) {
  const p = new Intl.DateTimeFormat('en-GB', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(iso));
  const g = (t: string) => p.find((x) => x.type === t)?.value ?? '';
  return { day: `${g('year')}-${g('month')}-${g('day')}`, hour: Number(g('hour')) };
}
const fmt = (iso: string | null, tz: string, opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' }) => (iso ? new Date(iso).toLocaleString([], { ...opts, timeZone: tz }) : '—');
const dayLabel = (day: string) => new Date(`${day}T12:00:00Z`).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });

function Tile({ icon, tone, value, label, sub }: { icon: ReactNode; tone: string; value: ReactNode; label: string; sub?: string }) {
  return (
    <div className="stat-tile">
      <span className={`stat-tile-icon ${tone}`}>{icon}</span>
      <span>
        <span className="stat-tile-label">{label}</span>
        <span className="stat-tile-value">{value}</span>
        {sub && <span className="stat-tile-sub">{sub}</span>}
      </span>
    </div>
  );
}

export function OperationReportPage() {
  const site = useSite();
  return <NeedsSite>{site && <Report key={site.id} siteId={site.id} />}</NeedsSite>;
}

function Report({ siteId }: { siteId: string }) {
  const { me } = useSession();
  const [params, setParams] = useSearchParams();
  const roomId = params.get('room') ?? '';
  const period = params.get('period') ?? (params.get('from') ? 'custom' : '7d');
  const rooms = useQuery({ queryKey: ['rooms', siteId, null], queryFn: () => api.get<{ items: Room[] }>(`/sites/${siteId}/rooms`) });
  const range = useMemo(() => {
    if (period === 'custom') return { from: params.get('from') ?? undefined, to: params.get('to') ?? undefined };
    const days = PERIODS.find((p) => p[0] === period)?.[2] ?? 7;
    const to = new Date();
    return { from: new Date(to.getTime() - days * 86_400_000).toISOString(), to: undefined as string | undefined };
  }, [period, params]);
  const report = useQuery({
    queryKey: ['operation', roomId, range.from, range.to],
    queryFn: () => api.get<Report>(`/rooms/${roomId}/operation?${new URLSearchParams(Object.entries(range).filter(([, v]) => v) as [string, string][]).toString()}`),
    enabled: !!roomId,
  });
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };
  const roomList = (rooms.data?.items ?? []).slice().sort((a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }));

  return (
    <>
      <PageHeader
        title="TV operation report"
        sub="Proof of what the TVs in a room did: when they were switched on, watched and used with the remote, and their state at check-in and check-out."
        actions={
          report.data && (
            <span className="no-print row" style={{ gap: 8 }}>
              <button className="btn" onClick={() => downloadCsv(report.data!)}>
                <Download size={16} /> Download CSV
              </button>
              <button className="btn primary" onClick={() => window.print()}>
                <Printer size={16} /> Print / save as PDF
              </button>
            </span>
          )
        }
      />
      <div className="filters no-print" style={{ marginBottom: 16 }}>
        <Select value={roomId} onChange={(e) => set({ room: e.target.value || null })} aria-label="Room">
          <option value="">Choose a room…</option>
          {roomList.map((r) => (
            <option key={r.id} value={r.id}>
              Room {r.number}
            </option>
          ))}
        </Select>
        <Select value={period} onChange={(e) => set({ period: e.target.value, from: null, to: null })} aria-label="Period">
          {PERIODS.map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
          {period === 'custom' && <option value="custom">Selected stay / dates</option>}
        </Select>
        {report.data && report.data.stays.length > 0 && (
          <Select
            value=""
            onChange={(e) => {
              const s = report.data!.stays.find((x) => x.id === e.target.value);
              if (s) set({ period: 'custom', from: new Date(new Date(s.checkInAt).getTime() - 86_400_000).toISOString(), to: s.checkOutAt ?? null });
            }}
            aria-label="Stay"
          >
            <option value="">Show one stay…</option>
            {report.data.stays.map((s) => (
              <option key={s.id} value={s.id}>
                {s.displayName ?? 'Guest'} · {fmt(s.checkInAt, report.data!.site.timezone, { dateStyle: 'medium' })}
              </option>
            ))}
          </Select>
        )}
      </div>
      {!roomId ? (
        <div className="card">
          <Empty>Choose a room to see what its TVs did. The server keeps this for every TV, every hour.</Empty>
        </div>
      ) : report.isLoading ? (
        <Loading />
      ) : report.data ? (
        <ReportBody r={report.data} by={me.user.name || me.user.email} />
      ) : null}
      <ErrorBox error={report.error ?? rooms.error} />
    </>
  );
}

function ReportBody({ r, by }: { r: Report; by: string }) {
  const tz = r.site.timezone;
  const sum = (k: keyof Hour) => r.hours.reduce((n, h) => n + (h[k] as number), 0);
  const powerOns = r.events.filter((e) => e.type === 'power_on');
  const errors = r.events.filter((e) => e.type === 'playback_error');
  const snapshots = r.events.filter((e) => e.type === 'stay_snapshot') as unknown as { id: string; deviceId: string; type: string; occurredAt: string; data: Snapshot }[];
  const devName = (id: string) => r.devices.find((d) => d.id === id)?.name ?? 'TV';

  // Days × hours grid in the hotel's time zone (all TVs of the room together).
  const days: string[] = [];
  for (let t = new Date(r.from).getTime(); t <= new Date(r.to).getTime() + 3_600_000; t += 3_600_000) {
    const d = local(new Date(t).toISOString(), tz).day;
    if (!days.includes(d)) days.push(d);
  }
  const cell = new Map<string, { on: number; watch: number; keys: number; zaps: number }>();
  for (const h of r.hours) {
    const l = local(h.hour, tz);
    const k = `${l.day}|${l.hour}`;
    const c = cell.get(k) ?? { on: 0, watch: 0, keys: 0, zaps: 0 };
    c.on = Math.min(3600, c.on + h.onlineSec);
    c.watch = Math.min(3600, c.watch + h.watchSec);
    c.keys += h.keyPresses;
    c.zaps += h.channelChanges;
    cell.set(k, c);
  }
  const perDay = days.map((d) => {
    let on = 0, watch = 0, keys = 0, zaps = 0, first = -1, last = -1;
    for (let h = 0; h < 24; h++) {
      const c = cell.get(`${d}|${h}`);
      if (!c) continue;
      on += c.on;
      watch += c.watch;
      keys += c.keys;
      zaps += c.zaps;
      if (c.on > 0) {
        if (first < 0) first = h;
        last = h;
      }
    }
    return { d, on, watch, keys, zaps, first, last, powerOns: powerOns.filter((e) => local(e.occurredAt, tz).day === d).length, errors: errors.filter((e) => local(e.occurredAt, tz).day === d).length };
  });

  return (
    <div className="op-report">
      <div className="card op-head">
        <div>
          <span className="muted">Room</span>
          <strong>
            {r.room.number}
            {r.room.zone ? ` · ${r.room.zone}` : ''}
          </strong>
        </div>
        <div>
          <span className="muted">Hotel</span>
          <strong>{r.site.name}</strong>
        </div>
        <div>
          <span className="muted">Period</span>
          <strong>
            {fmt(r.from, tz)} – {fmt(r.to, tz)}
          </strong>
        </div>
        <div>
          <span className="muted">Times shown in</span>
          <strong>{tz}</strong>
        </div>
      </div>

      <div className="stat-strip">
        <Tile icon={<Power size={22} />} tone="violet" value={powerOns.length} label="Switched on" sub="times in the period" />
        <Tile icon={<Clock3 size={22} />} tone="green" value={hm(sum('onlineSec'))} label="TV on" sub="app running" />
        <Tile icon={<MonitorPlay size={22} />} tone="blue" value={hm(sum('watchSec'))} label="Watching TV" sub="a channel was tuned" />
        <Tile icon={<Hand size={22} />} tone="amber" value={sum('keyPresses')} label="Remote presses" sub={`${sum('channelChanges')} channel changes`} />
        <Tile icon={<AlertTriangle size={22} />} tone={errors.length ? 'red' : 'grey'} value={errors.length} label="Playback errors" sub="streams that failed" />
      </div>

      {snapshots.length > 0 && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>TV state at check-in and check-out</h3>
          <div className="snap-grid">
            {snapshots.map((s) => {
              const d = s.data;
              return (
                <div key={s.id} className={`snap ${d.kind}`}>
                  <div className="row" style={{ gap: 8, flexWrap: 'nowrap' }}>
                    {d.kind === 'check_in' ? <LogIn size={18} /> : <LogOut size={18} />}
                    <strong>
                      {d.kind === 'check_in' ? 'Check-in' : 'Check-out'} · {fmt(s.occurredAt, tz)}
                    </strong>
                  </div>
                  <div className="muted" style={{ fontSize: 13 }}>
                    {d.guest ?? 'Guest'} · {devName(s.deviceId)}
                  </div>
                  <ul>
                    <li>
                      TV was <strong className={d.online ? 'ok' : 'bad'}>{d.online ? 'online and working' : 'offline'}</strong>
                      {d.lastSeenAt ? ` (last report ${fmt(d.lastSeenAt, tz, { timeStyle: 'short' })})` : ''}
                    </li>
                    <li>Last switched on: {fmt(d.switchedOnAt, tz)}</li>
                    <li>Playing: {d.channel ? `${d.channel.number} ${d.channel.name}` : '—'}</li>
                    <li>
                      Previous 24 h: on {hm(d.last24h.onlineSec)}, watched {hm(d.last24h.watchSec)}, {d.last24h.keyPresses} remote presses, {d.last24h.channelChanges} channel changes,{' '}
                      {d.last24h.playbackErrors} errors
                    </li>
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="card">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>Hour by hour</h3>
          <span className="op-legend">
            <i className="l0" /> off <i className="l1" /> on <i className="l2" /> watching <i className="l3" /> watching + remote used
          </span>
        </div>
        <div className="op-grid">
          <span />
          {Array.from({ length: 24 }, (_, h) => (
            <span key={h} className="op-hh">
              {h % 3 === 0 ? String(h).padStart(2, '0') : ''}
            </span>
          ))}
          {days.map((d) => (
            <FragmentRow key={d} day={d} cell={cell} />
          ))}
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Day by day</h3>
        <div className="table-wrap">
          <table className="fac-table">
            <thead>
              <tr>
                <th>Day</th>
                <th>Switched on</th>
                <th>TV on</th>
                <th>Watching</th>
                <th>Remote presses</th>
                <th>Channel changes</th>
                <th>First / last activity</th>
                <th>Errors</th>
              </tr>
            </thead>
            <tbody>
              {perDay.map((x) => (
                <tr key={x.d} style={!x.on ? { opacity: 0.55 } : undefined}>
                  <td>
                    <strong>{dayLabel(x.d)}</strong>
                  </td>
                  <td>{x.powerOns}</td>
                  <td>{x.on ? hm(x.on) : 'Off'}</td>
                  <td>{x.watch ? hm(x.watch) : '—'}</td>
                  <td>{x.keys}</td>
                  <td>{x.zaps}</td>
                  <td>{x.first >= 0 ? `${String(x.first).padStart(2, '0')}:00 – ${String(x.last + 1).padStart(2, '0')}:00` : '—'}</td>
                  <td>{x.errors || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="op-two">
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Timeline</h3>
          {powerOns.length + snapshots.length === 0 ? (
            <Empty>No power-on or check-in events in this period.</Empty>
          ) : (
            <div className="op-timeline">
              {[...powerOns, ...snapshots]
                .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))
                .slice(0, 60)
                .map((e) => (
                  <div key={e.id}>
                    {e.type === 'power_on' ? <Power size={15} /> : (e.data as unknown as Snapshot).kind === 'check_in' ? <LogIn size={15} /> : <LogOut size={15} />}
                    <span className="muted">{fmt(e.occurredAt, tz)}</span>
                    <span>
                      {e.type === 'power_on'
                        ? `${devName(e.deviceId)} switched on`
                        : `${(e.data as unknown as Snapshot).kind === 'check_in' ? 'Check-in' : 'Check-out'}: ${(e.data as unknown as Snapshot).online ? 'TV online' : 'TV offline'}`}
                    </span>
                  </div>
                ))}
            </div>
          )}
        </div>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Channels watched</h3>
          {r.channels.length === 0 ? (
            <Empty>No channel changes recorded.</Empty>
          ) : (
            <div className="fac-detail-list" style={{ borderTop: 0, marginTop: 0 }}>
              {r.channels.map((c) => (
                <div key={c.number} className="fac-detail-row">
                  <Zap size={15} />
                  <span style={{ color: 'var(--ink)', fontWeight: 600 }}>
                    {c.number} {c.name ?? ''}
                  </span>
                  <span className="fac-detail-val">{c.count}×</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>TVs in the room</h3>
        <div className="table-wrap">
          <table className="fac-table">
            <thead>
              <tr>
                <th>TV</th>
                <th>Model</th>
                <th>Serial / MAC</th>
                <th>App</th>
                <th>Now</th>
              </tr>
            </thead>
            <tbody>
              {r.devices.map((d) => (
                <tr key={d.id}>
                  <td>
                    <span className="row" style={{ gap: 8 }}>
                      <Tv size={16} /> <strong>{d.name}</strong>
                    </span>
                  </td>
                  <td>{[d.manufacturer, d.model].filter(Boolean).join(' ') || d.platform}</td>
                  <td className="mono" style={{ fontSize: 12 }}>
                    {d.serial ?? '—'} / {d.macAddress ?? '—'}
                  </td>
                  <td>{d.appVersion ?? '—'}</td>
                  <td>
                    <span className={`state-pill ${d.online ? 'online' : 'offline'}`}>
                      <span className="state-dot" /> {d.online ? 'Online' : `Offline since ${fmt(d.lastSeenAt, tz)}`}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="muted op-foot">
        <CalendarDays size={14} /> Generated {fmt(r.generatedAt, tz)} by {by} from the PulseIPTV server. Each TV reports to the server every 30 seconds while it is on; the server records the
        time on, the time a channel is tuned and remote presses per hour, when the TV is switched on, and the TV state at every check-in and check-out.
      </p>
    </div>
  );
}

function FragmentRow({ day, cell }: { day: string; cell: Map<string, { on: number; watch: number; keys: number; zaps: number }> }) {
  return (
    <>
      <span className="op-day">{dayLabel(day)}</span>
      {Array.from({ length: 24 }, (_, h) => {
        const c = cell.get(`${day}|${h}`);
        const lvl = !c || c.on < 60 ? 0 : c.watch >= 60 ? (c.keys > 0 ? 3 : 2) : 1;
        const tip = c ? `${String(h).padStart(2, '0')}:00 · on ${hm(c.on)}, watching ${hm(c.watch)}, ${c.keys} presses, ${c.zaps} channel changes` : `${String(h).padStart(2, '0')}:00 · off`;
        return <span key={h} className={`op-cell l${lvl}`} title={tip} />;
      })}
    </>
  );
}

function downloadCsv(r: Report) {
  const tz = r.site.timezone;
  const rows = [['Room', 'TV', 'Hour (' + tz + ')', 'On (min)', 'Watching (min)', 'Remote presses', 'Channel changes']];
  for (const h of r.hours) {
    rows.push([r.room.number, r.devices.find((d) => d.id === h.deviceId)?.name ?? '', new Date(h.hour).toLocaleString('sv-SE', { timeZone: tz }), String(Math.round(h.onlineSec / 60)), String(Math.round(h.watchSec / 60)), String(h.keyPresses), String(h.channelChanges)]);
  }
  rows.push([]);
  rows.push(['Event', 'TV', 'Time (' + tz + ')', 'Details']);
  for (const e of r.events) rows.push([e.type, r.devices.find((d) => d.id === e.deviceId)?.name ?? '', new Date(e.occurredAt).toLocaleString('sv-SE', { timeZone: tz }), JSON.stringify(e.data)]);
  const csv = rows.map((row) => row.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\r\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
  a.download = `tv-report-room-${r.room.number}-${r.to.slice(0, 10)}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}
